/**
 * The contract between the web app and a native shell hosting it.
 *
 * Three rules shape everything here.
 *
 * **Only timestamps, never durations.** A duration is wrong the moment it is sent, and the shell
 * may render it minutes later on a lock screen it did not wake for. A timestamp lets the platform
 * count down on its own — which is exactly what `Text(timerInterval:)` does — so a Live Activity
 * needs no update to stay honest.
 *
 * **The words come from here.** The shell renders `lead` and `detail`; it never builds a label
 * table of its own. `phaseLabel` already knows that `at_home` means "at school" on the ride home,
 * and the two places that reimplemented that mapping both got it wrong. There is deliberately no
 * station name in this payload for a shell to compose with.
 *
 * **The stale side is always the shell.** The web app is fetched and so is never the old one; the
 * binary can be a release behind. So both directions ignore what they do not recognise rather than
 * failing, and the version travels with the bridge so the web app can say the app needs rebuilding.
 */

import type { Direction } from "../core/sticky";
import type { RiderPhase } from "./types";

/**
 * Raised when a message gains a field the shell must understand to behave correctly.
 *
 * 3 gives activities distinct keys and lets an explicit backward phase declaration reopen one.
 * A shell on 2 would collapse private Pickup Planning into the home journey and ignore reopening,
 * so the page gates both behaviors on this version.
 */
export const NATIVE_BRIDGE_VERSION = 3;

/** The card's colour. The shell renders it; it does not work out what it should be. */
export type NativeTone = "good" | "warn" | "bad" | "info" | "";

/**
 * Which leg of the journey the number is about.
 *
 * A closed vocabulary rather than an icon name: the web app owns what the moment *is*, the shell
 * owns what that looks like on an Apple screen. Sending `tram.fill` would put a platform's symbol
 * catalogue in a React app, and sending the phase would put the label table in the shell.
 *
 * `wait` and `train` are separate because the compact island is a symbol and a number and nothing
 * else, and "13 min" beside the same tram means the train coming to him or him coming to the stop.
 * A shell that predates `wait` draws its fallback tram, which is what it drew before.
 */
export type NativeGlyph = "leave" | "drive" | "wait" | "train" | "";

/** The journey, in the web app's own terms and the card's own words. */
export interface NativeJourney {
	phase: RiderPhase;
	direction: Direction;
	/** Overrides the ordinary service-day/direction identity for a distinct native activity. */
	activityKey?: string;
	/** One-shot instruction from an explicit backward phase declaration. */
	reopenActivity?: boolean;
	/**
	 * Whether the web app currently has a reason to spend power on native location updates.
	 *
	 * The one field here that is an instruction rather than a description, which is why it is optional
	 * and why the shell reads an absent value as "keep running". The page always sets it; the server
	 * re-renders this same object to correct a lock screen and has no opinion about somebody's GPS,
	 * so it leaves it off rather than inventing an answer.
	 */
	locationActive?: boolean;
	/** Service day, so the shell can tell one morning's activity from the next. */
	dayKey: string;
	/** When the web app worked this out, so the shell can show its age instead of guessing. */
	atMs: number;
	/** The small line above the number, e.g. `LEAVE IN` or `CIVIC CENTER IN`. */
	lead: string;
	/**
	 * The same line at Dynamic Island width, e.g. `CIVC CTR in`.
	 *
	 * The expanded island truncates a lead at roughly eight characters, so the station is abbreviated
	 * by hand rather than clipped. Both are worded here for the same reason everything else is: the
	 * shell owns no label table, and picking which to draw is a layout decision, not a wording one.
	 */
	leadShort: string;
	/** A static primary value such as `NOW`, used when there is no instant to count down to. */
	value: string | null;
	/** The line below it: the card's own sub, e.g. `7:35 am train`. */
	detail: string | null;
	/**
	 * The margin beside the number, e.g. `10 min spare`.
	 *
	 * Safe to send as text because it is a property of the plan rather than of the clock: it is two
	 * timestamps subtracted, so it does not decay while the phone sleeps. It changes when the plan
	 * does, and a new plan sends a new journey.
	 */
	note: string | null;
	tone: NativeTone;
	glyph: NativeGlyph;
	/** The instant to count down to. Null when no number is worth showing. */
	countdownToMs: number | null;
}

/**
 * One train on the watch's deck, already worded.
 *
 * The same rule as the journey above: there is no departure timestamp to format and no station id
 * to look up, because formatting a clock and naming a station are both things the web app has
 * already done once. A watch that did either would be the third implementation of `stationLabel`.
 *
 * `pinned` and `recommended` are separate and both are sent. They are usually the same train and
 * the day they are not is the day the deck earns its place: the engine has moved on to a later
 * departure and he is standing on the platform for the earlier one.
 */
export interface NativeWatchTrain {
	planId: string;
	/** The departure, e.g. `7:35 am`. */
	when: string;
	/** Where it is going, e.g. `Rockridge`. */
	lead: string;
	/** The line under it, e.g. `Yellow · 24 min ride`. */
	detail: string | null;
	/** The margin, e.g. `12 min spare`. */
	note: string | null;
	tone: NativeTone;
	pinned: boolean;
	recommended: boolean;
	/** False when he cannot physically make it. The card is dimmed, not hidden — see the deck. */
	reachable: boolean;
	/** The instant this card's number is about. Null when there is nothing worth counting. */
	countdownToMs: number | null;
}

/** The afternoon meeting from the rider's side, in the words his own screen uses. */
export interface NativeWatchPickup {
	lead: string;
	value: string | null;
	detail: string | null;
	note: string | null;
	tone: NativeTone;
	countdownToMs: number | null;
	/** The agreed kerb, e.g. `North - Hyde St.`. Null until one has been set. */
	spot: string | null;
}

/**
 * Everything a watch may show, composed here.
 *
 * `now` is the Live Activity's own object rather than a copy of its fields: the wrist and the lock
 * screen are the same glance, and two objects would eventually word it two ways. What the watch adds
 * is room — `context` is the line the island has no width for — and pages, which are `trains` and
 * `pickup` being present at all.
 *
 * Which pages to draw is left to the watch, because that is arranging rather than wording. An empty
 * `trains` means there is no deck worth swiping, not that the watch should invent one.
 */
export interface NativeWatch {
	/** When this was worked out, so a raised wrist can show its age instead of guessing. */
	atMs: number;
	now: NativeJourney;
	/** The extra line, e.g. `Next 12th St · Rockridge in 12 mins`. */
	context: string | null;
	trains: NativeWatchTrain[];
	pickup: NativeWatchPickup | null;
}

/** What the shell may report. Each verb is a category registered in the binary. */
export type NativeAction = "aboard" | "snooze" | "picked-up";

/**
 * The rider confirmed a train on his wrist.
 *
 * Deliberately not a `NativeAction`: those verbs are notification categories compiled into the
 * binary, and this is a gesture on a different device that carries a plan id with it.
 *
 * Like `NativeActionReport` it states its own time. A watch tap can be relayed through a phone that
 * was asleep when it happened, and a late claim read as a present one is the fault that replayed a
 * morning's arrival at 15:23.
 */
export interface NativePinRequest {
	planId: string;
	atMs: number;
}

/**
 * An assertion the rider made, and when.
 *
 * The time is not optional and is not the delivery time. A lock-screen tap can happen while the
 * webview is suspended and reach the page minutes later, and a late assertion read as a present one
 * is the same fault as the cold open that replayed the morning's "arrived school" at 15:23.
 */
export interface NativeActionReport {
	action: NativeAction;
	atMs: number;
}

export interface NativeFix {
	lat: number;
	lon: number;
	accuracyMeters: number;
	speedMetersPerSecond: number | null;
	atMs: number;
}

/** Something the shell could not make sense of, so it can be seen in diagnostics. */
export interface NativeFault {
	reason: string;
	atMs: number;
}

/**
 * The shell's APNs device token.
 *
 * A `WKWebView` can never hold a web push subscription, so this is the only way a closed phone is
 * reached without the Home-Screen PWA. The page passes it to `/api/alerts` in place of a
 * subscription, which is what puts the shell into the same sweep every other device is in.
 *
 * `environment` decides which of Apple's two gateways the token is valid at, and a token sent to
 * the wrong one fails as `BadDeviceToken`. The shell knows which entitlement it was signed with;
 * the server cannot work it out, so it is not asked to guess.
 */
export interface NativePushToken {
	/** Lowercase hex, as Apple issues it. */
	token: string;
	environment: "development" | "production";
}

/**
 * The push token of one running Live Activity.
 *
 * Not the same address as `NativePushToken`, and the difference is the whole reason the pin is a
 * separate record. That one reaches the app and lives as long as the install; this one reaches a
 * single card, is minted by ActivityKit when the card starts, is reissued while it runs, and dies
 * with it. Sending an update to the device token silently does nothing.
 *
 * `journeyKey` names the card the token belongs to, so a token arriving late from yesterday's
 * activity cannot be attached to today's.
 */
export interface NativeActivityToken {
	token: string;
	environment: "development" | "production";
	journeyKey: string;
}

/** How a card is named on both sides of the bridge. Mirrors `Journey.key` in `Bridge.swift`. */
export function journeyKeyOf(journey: Pick<NativeJourney, "dayKey" | "direction" | "activityKey">): string {
	return journey.activityKey ?? `${journey.dayKey}-${journey.direction}`;
}

/**
 * Whether this token addresses the card this journey would raise.
 *
 * An activity's token sequence outlives the activity, so a card ended at the drop-off can still
 * issue one. The server keeps only the newest, so accepting a late token would replace the address
 * of the card on screen with the address of one that is gone — silently, and for the rest of the
 * day. The key was carried down the bridge for exactly this and nothing was reading it.
 */
export function activityTokenIsFor(token: NativeActivityToken | null, journey: Pick<NativeJourney, "dayKey" | "direction" | "activityKey"> | null): boolean {
	return token != null && journey != null && token.journeyKey === journeyKeyOf(journey);
}

/**
 * Feedback only the shell can produce, because iOS Safari has no Vibration API.
 *
 * `intensity` is the rider's own setting, sent with every buzz. iOS has no system haptic volume to
 * read or scale, so the strength has to be synthesised on the shell side from this number.
 *
 * Deliberately does not raise `NATIVE_BRIDGE_VERSION`: an older shell ignores a message type it
 * has never heard of, and a missing buzz is not worth telling the rider their app is stale.
 */
export type NativeHaptic = "selection" | "impact" | "warning" | "alert";

/**
 * A notification for the shell to raise, in the web app's own words.
 *
 * Web push does not work in a `WKWebView`, so this is the shell's only way of reaching a locked
 * phone — and unlike push it needs no paid account. `atMs` is the instant it should fire, never a
 * delay: the message may be handled a moment after it was sent.
 */
export interface NativeReminder {
	/** Replaces an earlier reminder with the same id rather than stacking another on the screen. */
	id: string;
	title: string;
	body: string;
	atMs: number;
}

export type NativeJourneyMessage = { type: "journey"; journey: NativeJourney };
/**
 * The wrist, sent alongside the journey rather than instead of it.
 *
 * A new message type rather than fields on `journey`, and so **`NATIVE_BRIDGE_VERSION` does not
 * move**: the rule is that it rises when an existing message gains something the shell must
 * understand, and a shell that has never heard of this one ignores it and shows exactly what it
 * shows today. Marking both installed phones stale over a watch neither of them is paired with
 * would be the version notice crying wolf.
 */
export type NativeWatchMessage = { type: "watch"; watch: NativeWatch };
export type NativeHapticMessage = { type: "haptic"; haptic: NativeHaptic; intensity?: number };
export type NativeReminderMessage = { type: "reminder"; reminder: NativeReminder };
/**
 * Who the shell is, as far as the server is concerned.
 *
 * The device id lives in the page's storage, so the shell cannot know it without being told — and
 * it needs it to report fixes while the page is asleep, which is the only way the background gap
 * is ever closed. A shell that invented its own id would be a third phone.
 */
export type NativeIdentityMessage = { type: "identity"; deviceId: string };

/**
 * Ask iOS for permission to show notifications.
 *
 * A push with no authorisation is delivered and shows nothing, which is indistinguishable from one
 * that never arrived. The web `Notification.requestPermission` does not exist in a `WKWebView`, so
 * the ask has to be made on the native side — at the moment the rider turns alerts on, not at
 * launch before they have seen the app.
 */
export type NativePermissionMessage = { type: "ask-notifications" };

export type NativeOutbound =
	| NativeJourneyMessage
	| NativeWatchMessage
	| NativeHapticMessage
	| NativeReminderMessage
	| NativeIdentityMessage
	| NativePermissionMessage;

export type NativeInbound =
	| { type: "fix"; fix: NativeFix }
	| { type: "action"; report: NativeActionReport }
	| { type: "pin"; pin: NativePinRequest }
	| { type: "push-token"; push: NativePushToken }
	| { type: "activity-token"; activity: NativeActivityToken }
	| { type: "fault"; fault: NativeFault };

const ACTIONS: readonly NativeAction[] = ["aboard", "snooze", "picked-up"];

function finiteOrNull(value: unknown): number | null {
	return typeof value === "number" && Number.isFinite(value) ? value : null;
}

/**
 * Decode something the shell said.
 *
 * Returns null for anything unrecognised — an older or newer shell, a message type this build has
 * never heard of, or malformed JSON. A message we cannot read is not an error worth surfacing; it
 * is a build mismatch, and the version notice is what tells the rider about those.
 */
export function parseNativeMessage(raw: unknown): NativeInbound | null {
	const value = typeof raw === "string" ? safeParse(raw) : raw;
	if (!value || typeof value !== "object") {
		return null;
	}
	const message = value as Record<string, unknown>;

	if (message.type === "fix") {
		const fix = message.fix;
		if (!fix || typeof fix !== "object") {
			return null;
		}
		const candidate = fix as Record<string, unknown>;
		const lat = finiteOrNull(candidate.lat);
		const lon = finiteOrNull(candidate.lon);
		const accuracyMeters = finiteOrNull(candidate.accuracyMeters);
		const atMs = finiteOrNull(candidate.atMs);
		// A fix without a position, an accuracy, or a time cannot be reasoned about at all.
		if (lat == null || lon == null || accuracyMeters == null || atMs == null) {
			return null;
		}
		return {
			type: "fix",
			fix: { lat, lon, accuracyMeters, speedMetersPerSecond: finiteOrNull(candidate.speedMetersPerSecond), atMs },
		};
	}

	if (message.type === "action") {
		const action = ACTIONS.find((known) => known === message.action);
		const atMs = finiteOrNull(message.atMs);
		// An assertion with no time cannot be placed in the journey, so it is not an assertion.
		return action && atMs != null ? { type: "action", report: { action, atMs } } : null;
	}

	if (message.type === "pin") {
		const planId = typeof message.planId === "string" && message.planId.length > 0 ? message.planId.slice(0, 64) : null;
		const atMs = finiteOrNull(message.atMs);
		// Naming no train is not a pin, and the page has no way to guess which one was under the finger.
		return planId && atMs != null ? { type: "pin", pin: { planId, atMs } } : null;
	}

	if (message.type === "fault") {
		const reason = typeof message.reason === "string" && message.reason.length > 0 ? message.reason : null;
		const atMs = finiteOrNull(message.atMs);
		return reason && atMs != null ? { type: "fault", fault: { reason, atMs } } : null;
	}

	if (message.type === "push-token") {
		const token = typeof message.token === "string" ? message.token.trim().toLowerCase() : "";
		const environment = message.environment === "production" ? "production" : "development";
		// Apple issues hex. Anything else is a shell bug, and storing it would buy a row that can
		// only ever fail to deliver.
		return /^[0-9a-f]{16,200}$/.test(token) ? { type: "push-token", push: { token, environment } } : null;
	}

	if (message.type === "activity-token") {
		const token = typeof message.token === "string" ? message.token.trim().toLowerCase() : "";
		const environment = message.environment === "production" ? "production" : "development";
		const journeyKey = typeof message.journeyKey === "string" ? message.journeyKey.slice(0, 64) : "";
		// Without the key there is no way to tell a token for the card on screen from one arriving
		// late for a card that has already ended, and attaching the wrong one silences the right one.
		if (!journeyKey || !/^[0-9a-f]{16,200}$/.test(token)) {
			return null;
		}
		return { type: "activity-token", activity: { token, environment, journeyKey } };
	}

	return null;
}

function safeParse(raw: string): unknown {
	try {
		return JSON.parse(raw);
	} catch {
		return null;
	}
}

const TONES: readonly NativeTone[] = ["good", "warn", "bad", "info", ""];
const GLYPHS: readonly NativeGlyph[] = ["leave", "drive", "wait", "train", ""];
const PHASES: readonly RiderPhase[] = [
	"planning",
	"at_home",
	"driving",
	"at_station",
	"on_platform",
	"on_train",
	"at_destination",
	"arrived",
];

/**
 * Read a journey off the wire.
 *
 * The shell is not the only thing that receives one of these any more: the server takes the same
 * object when a page pins a journey, and then re-renders it every minute from its own snapshot.
 * That makes this a validator for something that ends up on a lock screen, so anything unrecognised
 * is refused rather than coerced — a card that says the wrong thing is worse than no card.
 *
 * Lengths are capped because the words go into a 4 KB APNs payload and a truncated content state is
 * a push Apple discards without telling anyone.
 */
export function parseNativeJourney(raw: unknown): NativeJourney | null {
	if (!raw || typeof raw !== "object") {
		return null;
	}
	const value = raw as Record<string, unknown>;
	const phase = PHASES.find((known) => known === value.phase);
	const direction = value.direction === "home" ? "home" : value.direction === "school" ? "school" : null;
	const dayKey = typeof value.dayKey === "string" && /^\d{8}$/.test(value.dayKey) ? value.dayKey : null;
	const activityKey = value.activityKey == null
		? undefined
		: typeof value.activityKey === "string" && /^[a-z0-9-]{1,64}$/.test(value.activityKey)
			? value.activityKey
			: null;
	const atMs = finiteOrNull(value.atMs);
	const lead = typeof value.lead === "string" && value.lead.length > 0 ? value.lead.slice(0, 64) : null;
	if (!phase || !direction || !dayKey || activityKey === null || atMs == null || !lead) {
		return null;
	}
	return {
		phase,
		direction,
		...(activityKey ? { activityKey } : {}),
		dayKey,
		atMs,
		lead,
		leadShort: typeof value.leadShort === "string" && value.leadShort.length > 0 ? value.leadShort.slice(0, 32) : lead,
		value: typeof value.value === "string" && value.value.length > 0 ? value.value.slice(0, 32) : null,
		detail: typeof value.detail === "string" && value.detail.length > 0 ? value.detail.slice(0, 96) : null,
		note: typeof value.note === "string" && value.note.length > 0 ? value.note.slice(0, 48) : null,
		tone: TONES.find((known) => known === value.tone) ?? "",
		glyph: GLYPHS.find((known) => known === value.glyph) ?? "",
		countdownToMs: finiteOrNull(value.countdownToMs),
	};
}

export function journeyMessage(journey: NativeJourney): NativeJourneyMessage {
	return { type: "journey", journey };
}

export function watchMessage(watch: NativeWatch): NativeWatchMessage {
	return { type: "watch", watch };
}

export function hapticMessage(haptic: NativeHaptic, intensity?: number): NativeHapticMessage {
	return intensity == null ? { type: "haptic", haptic } : { type: "haptic", haptic, intensity };
}

export function reminderMessage(reminder: NativeReminder): NativeReminderMessage {
	return { type: "reminder", reminder };
}

export function identityMessage(deviceId: string): NativeIdentityMessage {
	return { type: "identity", deviceId };
}

export function askNotificationsMessage(): NativePermissionMessage {
	return { type: "ask-notifications" };
}

/**
 * Whether the installed shell predates the contract this build speaks.
 *
 * Both phones are ours, so an out-of-date shell is a rebuild we owe rather than a user we have to
 * accommodate silently. The web app still works — it degrades to what a browser does — but it says
 * so, because a morning lost to a quietly degraded app is worse than a line of text.
 */
export function isStaleShell(shellVersion: number | null): boolean {
	return shellVersion != null && shellVersion < NATIVE_BRIDGE_VERSION;
}
